import { useState, type FormEvent, type ReactNode } from 'react';
import { Card, ErrorBanner, Loading, Spinner } from '../../components/ui.js';
import { api } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { useAction, useAsync } from '../../lib/hooks.js';
import { AdminLayout } from './AdminLayout.js';

interface Gateway {
  id: string;
  provider: string;
  label: string;
  environment: 'test' | 'live';
  active: boolean;
  credentialHints: Record<string, string>;
  lastTestedAt: string | null;
  lastTestOk: boolean | null;
  lastTestError: string | null;
  createdAt: string;
}

interface GatewayResponse {
  data: Gateway[];
  supportedProviders: Record<string, string[]>;
}

/**
 * Payment gateway configuration.
 *
 * Credentials are write-only by design: the server stores them encrypted and
 * has no endpoint that returns them, so this screen can show which key is
 * configured but never what it is. Replacing one means entering the whole
 * bundle again.
 */
export function AdminGatewaysPage(): ReactNode {
  return (
    <AdminLayout>{(staff) => <Gateways canWrite={staff.role === 'superadmin'} />}</AdminLayout>
  );
}

function Gateways({ canWrite }: { canWrite: boolean }): ReactNode {
  const gateways = useAsync(() => api.get<GatewayResponse>('/api/admin/gateways'), []);
  const { run, pending, error } = useAction();
  const [showForm, setShowForm] = useState(false);

  if (gateways.loading) return <Loading />;

  const providers = gateways.data?.supportedProviders ?? {};
  const list = gateways.data?.data ?? [];

  async function activate(id: string): Promise<void> {
    await run(async () => {
      await api.post(`/api/admin/gateways/${id}/activate`);
      gateways.reload();
    });
  }

  async function test(id: string): Promise<void> {
    await run(async () => {
      await api.post(`/api/admin/gateways/${id}/test`);
      gateways.reload();
    });
  }

  async function remove(id: string): Promise<void> {
    await run(async () => {
      await api.delete(`/api/admin/gateways/${id}`);
      gateways.reload();
    });
  }

  return (
    <div className="stack">
      <ErrorBanner error={gateways.error} />
      <ErrorBanner message={error} />

      <div className="banner banner-warning">
        Credentials are encrypted before they are stored and cannot be read back through any
        endpoint — only replaced. Keep a copy wherever you normally keep secrets.
      </div>

      {list.length === 0 ? (
        <Card>
          <div className="empty">
            <h2>No gateway configured</h2>
            <p className="muted small">
              Billing is using the provider from environment configuration. Register one here to
              manage it without a redeploy.
            </p>
          </div>
        </Card>
      ) : (
        list.map((gateway) => (
          <Card key={gateway.id}>
            <div className="row">
              <div>
                <div className="row" style={{ gap: '0.5rem' }}>
                  <strong>{gateway.label}</strong>
                  <span className="badge">{gateway.provider}</span>
                  <span
                    className={`badge ${gateway.environment === 'live' ? 'badge-warning' : ''}`}
                  >
                    {gateway.environment}
                  </span>
                  {gateway.active && <span className="badge badge-success">Active</span>}
                </div>
                <p className="small faint" style={{ marginTop: '0.3rem' }}>
                  Added {formatDate(gateway.createdAt)}
                </p>
              </div>
              {canWrite && (
                <div className="button-row" style={{ marginLeft: 'auto' }}>
                  {!gateway.active && (
                    <button
                      type="button"
                      className="button-secondary"
                      disabled={pending}
                      onClick={() => void activate(gateway.id)}
                    >
                      Make active
                    </button>
                  )}
                  {gateway.active && (
                    <button
                      type="button"
                      className="button-secondary"
                      disabled={pending}
                      onClick={() => void test(gateway.id)}
                    >
                      Test connection
                    </button>
                  )}
                  {!gateway.active && (
                    <button
                      type="button"
                      className="button-danger"
                      disabled={pending}
                      onClick={() => void remove(gateway.id)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}
            </div>

            <table style={{ marginTop: '0.75rem' }}>
              <tbody>
                {Object.entries(gateway.credentialHints).map(([field, hint]) => (
                  <tr key={field}>
                    <td className="muted small">{field}</td>
                    <td className="mono">{hint}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {gateway.lastTestedAt && (
              <p className="small" style={{ marginTop: '0.6rem' }}>
                {gateway.lastTestOk ? (
                  <span className="badge badge-success">Connection ok</span>
                ) : (
                  <span className="badge badge-danger">Failed</span>
                )}{' '}
                <span className="faint">tested {formatDate(gateway.lastTestedAt)}</span>
                {gateway.lastTestError && <span className="faint"> — {gateway.lastTestError}</span>}
              </p>
            )}
          </Card>
        ))
      )}

      {canWrite &&
        (showForm ? (
          <GatewayForm
            providers={providers}
            onDone={() => {
              setShowForm(false);
              gateways.reload();
            }}
            onCancel={() => setShowForm(false)}
          />
        ) : (
          <div>
            <button type="button" className="button-primary" onClick={() => setShowForm(true)}>
              Add a gateway
            </button>
          </div>
        ))}
    </div>
  );
}

function GatewayForm({
  providers,
  onDone,
  onCancel,
}: {
  providers: Record<string, string[]>;
  onDone: () => void;
  onCancel: () => void;
}): ReactNode {
  const providerNames = Object.keys(providers);
  const [provider, setProvider] = useState(providerNames[0] ?? 'razorpay');
  const [label, setLabel] = useState('');
  const [environment, setEnvironment] = useState<'test' | 'live'>('test');
  const [activate, setActivate] = useState(false);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const { run, pending, error } = useAction();

  const fields = providers[provider] ?? [];

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await run(async () => {
      await api.post('/api/admin/gateways', {
        provider,
        label,
        environment,
        credentials,
        activate,
      });
      onDone();
    });
  }

  return (
    <Card title="Add a payment gateway">
      <form onSubmit={(e) => void submit(e)} className="stack">
        <ErrorBanner message={error} />

        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="gw-provider">Provider</label>
            <select
              id="gw-provider"
              value={provider}
              onChange={(e) => {
                setProvider(e.target.value);
                setCredentials({});
              }}
            >
              {providerNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="gw-env">Environment</label>
            <select
              id="gw-env"
              value={environment}
              onChange={(e) => setEnvironment(e.target.value as 'test' | 'live')}
            >
              <option value="test">test</option>
              <option value="live">live</option>
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="gw-label">Label</label>
          <input
            id="gw-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Razorpay (test)"
            required
          />
        </div>

        {fields.map((field) => (
          <div key={field}>
            <label htmlFor={`gw-${field}`}>{field}</label>
            <input
              id={`gw-${field}`}
              // Never a plain text input: these are secrets, and the browser
              // should not offer to remember or autofill them.
              type={field === 'keyId' ? 'text' : 'password'}
              autoComplete="off"
              value={credentials[field] ?? ''}
              onChange={(e) => setCredentials({ ...credentials, [field]: e.target.value })}
              required
            />
          </div>
        ))}

        <label className="row" style={{ gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={activate}
            onChange={(e) => setActivate(e.target.checked)}
            style={{ width: 'auto' }}
          />
          <span>Make this the active gateway</span>
        </label>

        <div className="button-row">
          <button type="submit" className="button-primary" disabled={pending}>
            {pending && <Spinner />}
            Save gateway
          </button>
          <button type="button" className="button-secondary" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </Card>
  );
}
